import Image from "next/image";
import Link from "next/link";
import Layout from "../layout/layout";
import TrendingNow from "../core/trending-now";
import SideBar from "../sidebar/sidebar";
import NewsActionButtons from "../core/news-action-buttons";
import useDateLocale from "hooks/useDateLocale";
import StickyBox from "react-sticky-box";
import SwitchToAppDialog from "../core/switch-to-app-dialog";
import NewNewsCard from "../core/new-news-card";
import NewsStackOnNews from "@public/news-stack-on-news.png";
import useOnScreen from "hooks/useOnScreen";
import { formatDistanceToNowStrict } from "date-fns";
import { NewsPageQuery } from "@graphql/generated/graphql";
import { useMemo, useRef } from "react";
import { Trans } from "@lingui/macro";
import { useRouter } from "next/router";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { generateSubdomainPath } from "../layout/shared/link-props";
import { DialogName, triggerDialog } from "@components/core/dialog";
import { topCategories } from "../sidebar/side-bar-topics";
import ReadWallEn from "@public/read_wall_en.png";
import ReadWallMs from "@public/read_wall_ms.png";
import ReadWallZh from "@public/read_wall_zh.png";
import dynamic from "next/dynamic";
import SponsoredCarousel, {
  SponsoredCarouselVariant,
  SponsoredClientDataElementType,
} from "@components/sponsored/sponsored-carousel";
import SponsoredCard from "@components/sponsored/sponsored-card";
import SideBarTopNews from "../sidebar/side-bar-top-news";
import { LandingDownloadStarRatings } from "../landing-page/landing-download-cta";
import NewsPaas from "./news-paas";

const CommentsBox = dynamic(() => import("@components/comments/comments-box"));

interface INewsSummaryPageProps {
  data: NewsPageQuery;
  loading: Boolean;
  fetchMore: any;
  limit: number;
  onelink: string;
}

const NewsSummaryPage: React.FunctionComponent<INewsSummaryPageProps> = ({
  data,
  loading,
  limit,
  fetchMore,
  onelink,
}) => {
  const switchToApp = onelink;
  const ref = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const refValue = useOnScreen(ref, router)[1];

  if (!loading && data && !data.news) {
    router.push("/news?topic=Trending&contentNotFound=true");
    return <></>;
  }

  return (
    <Layout
      showNewsStacks={true}
      showCTA={false}
      showFooter={false}
      className="!overflow-visible"
      retainWholePage={false}
      refValue={refValue}
    >
      <div className="mx-auto my-6 flex max-w-7xl flex-row items-start px-4 2xs:px-6 lg:space-x-20">
        {loading || !data ? (
          <p className="flex-1 py-4 text-center font-sen text-text-lightest">
            <Trans>Loading...</Trans>
          </p>
        ) : data.news ? (
          <div>
            <NewsSummary news={data.news} onelink={onelink} paas={data.paas} />
            <div className="mt-8 lg:hidden">
              {data?.news?.commentsCount > 0 && (
                <CommentsBox
                  deeplink={switchToApp}
                  comments={data?.news?.comments}
                  commentsCount={data?.news?.commentsCount}
                  contentId={data?.news?.id}
                  contentType={"News"}
                  limit={limit}
                  fetchMore={fetchMore}
                />
              )}
            </div>
            <div>
              <div
                className="mt-12 font-poppins text-xl font-bold text-text-darkest"
                id="might-like-section"
              >
                {data.news.nextNews?.length > 0 && (
                  <>
                    {data.news.isUnderNewsStack ? (
                      <Trans>More articles in this stack</Trans>
                    ) : (
                      <Trans>You might like ⬇️</Trans>
                    )}
                    <div className="mt-4 h-[1px] w-full bg-off-white"></div>
                  </>
                )}
                {data.news.nextNews && (
                  <NewsListSection news={data.news.nextNews} />
                )}
              </div>
            </div>
            <div>
              <div className="mt-12 font-poppins text-xl font-bold text-text-darkest">
                {data.news.newsPublisher?.news.length > 0 && (
                  <>
                    <Trans>📖 Also from this publisher</Trans>
                    <div className="mt-4 h-[1px] w-full bg-off-white"></div>
                  </>
                )}
                {data.news.newsPublisher?.news && (
                  <NewsListSection news={data.news.newsPublisher.news} />
                )}
              </div>
            </div>
            <div className="relative my-16 md:my-8 md:flex">
              <div className="relative h-80 w-full md:h-[348px] md:w-[344px]">
                <Image
                  className="rounded-lg"
                  src={NewsStackOnNews}
                  alt="news-stack-on-news-image"
                  layout="fill"
                  objectFit="cover"
                />
              </div>

              <div className="flex flex-1 flex-col md:pl-6">
                <p className="mt-8 font-poppins text-3xl font-bold leading-[1.3] md:mb-0">
                  <Trans>Why read with Lumi?</Trans>
                </p>
                <p className="font-sen font-light text-text-medium">
                  <Trans>
                    Being in the know can be hard when we're drowning in a sea
                    of noise and information. Curate what you read and catch up
                    in much less time with the Lumi app.
                  </Trans>
                </p>
                <div className="flex space-x-4">
                  <Link href={switchToApp}>
                    <a className="mt-8 inline-flex">
                      <div className="flex items-center rounded-2xl bg-primary px-4 py-2">
                        <p className="text-center font-sen text-sm text-white">
                          <Trans>Download Now</Trans>
                        </p>
                      </div>
                    </a>
                  </Link>
                  <Link href={generateSubdomainPath("/why-use-lumi")}>
                    <a className="mt-8 inline-flex">
                      <div className="flex items-center rounded-2xl bg-secondary px-4 py-2">
                        <p className="text-center font-sen text-sm text-primary2">
                          <Trans>Learn more</Trans>
                        </p>
                        <ChevronRightIcon
                          className="h-6 w-6 text-primary2"
                          aria-hidden="true"
                        />
                      </div>
                    </a>
                  </Link>
                </div>
              </div>
            </div>
            <div className="block lg:hidden">
              <SideBarTopNews />
            </div>
            <div className="-mx-4 mt-8" ref={ref}>
              <TrendingNow />
            </div>
          </div>
        ) : (
          <p className="flex-1 py-4 text-center font-sen text-text-lightest">
            <Trans>No news found</Trans>
          </p>
        )}

        <StickyBox offsetTop={20} offsetBottom={20} className="space-y-10">
          <div className="hidden lg:block">
            {data?.news?.commentsCount > 0 && (
              <CommentsBox
                deeplink={switchToApp}
                comments={data?.news?.comments}
                commentsCount={data?.news?.commentsCount}
                contentId={data?.news?.id}
                contentType={"News"}
                limit={limit}
                fetchMore={fetchMore}
                loading={loading}
              />
            )}
            <div className="mt-8 hidden lg:block">
              <SideBarTopNews />
            </div>
          </div>
          <SideBar />
        </StickyBox>
      </div>
    </Layout>
  );
};

interface NewsSummaryProps {
  news: NewsPageQuery["news"];
  paas: NewsPageQuery["paas"];
  onelink: string;
}

const NewsSummary: React.FunctionComponent<NewsSummaryProps> = ({
  news,
  paas,
  onelink,
}) => {
  const sponsoredContent = news.clientData;

  const { content, title, imageUrl, pubDate, newsPublisher, link, topics } =
    news;

  const utm_link = useMemo(() => {
    const url = new URL(link);

    url.searchParams.set("utm_source", "lumi");
    url.searchParams.set("utm_medium", "lumi_web");
    url.searchParams.set("utm_campaign", "article");

    return url.toString();
  }, [link]);

  const locale = useDateLocale();

  const openMoreTopicsDialog = (event) => {
    event.preventDefault();
    triggerDialog(DialogName.moreTopics);
  };

  return (
    <>
      <h1 className="font-poppins text-3xl font-bold text-text-darkest">
        {title}
      </h1>

      <div className="my-4 flex flex-row flex-wrap gap-2">
        {topics &&
          topics.map((topic) => (
            <Link
              href={`/news?topic=${topic.key || ""}`}
              key={topic.id}
              passHref
            >
              <a className="flex-shrink-0">
                <div
                  className={`rounded bg-primary2 px-2 py-1 font-sen text-xs text-white ${
                    topCategories.some((category) => category.key == topic.key)
                      ? ""
                      : "cursor-pointer"
                  }`}
                  onClick={(event) =>
                    !topCategories.some(
                      (category) => category.key == topic.key
                    ) && openMoreTopicsDialog(event)
                  }
                >
                  {topic.name} {topic.emoji}
                </div>
              </a>
            </Link>
          ))}
      </div>

      <div className="relative">
        <div className="relative h-52 w-full md:h-[434px]">
          <Image
            className="rounded-lg"
            src={imageUrl}
            alt={title}
            layout="fill"
            objectFit="cover"
            priority
          />
        </div>

        <NewsPaas paas={paas} />

        <div className="2xs:flex 2xs:items-center 2xs:justify-between">
          <div
            className="my-6 flex items-center justify-between"
            id="publisher-name"
          >
            <div className="relative h-[30px] w-[53px] drop-shadow-custom">
              <Link href={`/publishers/${newsPublisher.slug}`}>
                <a>
                  <Image
                    className="rounded-lg"
                    src={newsPublisher.imageUrl}
                    alt={newsPublisher.name}
                    layout="fill"
                    objectFit="cover"
                    priority
                  />
                </a>
              </Link>
            </div>

            <div className="ml-4 flex-1">
              <Link href={`/publishers/${newsPublisher.slug}`}>
                <a>
                  <span className="font-poppins text-sm font-bold">
                    {newsPublisher.name}
                  </span>
                </a>
              </Link>
            </div>
          </div>

          <p className="mb-4 font-sen text-xs text-text-medium 2xs:mb-0">
            {formatDistanceToNowStrict(new Date(pubDate), {
              locale,
              addSuffix: true,
            })}
          </p>
        </div>

        <div
          className="news-content break-words font-poppins text-text-dark"
          dangerouslySetInnerHTML={{ __html: content }}
        ></div>

        <div className="absolute inset-0 top-[80%] bottom-0 bg-gradient-to-b from-white/0 to-white"></div>
      </div>
      <div className="relative z-10 bg-white">
        <ReadMoreSection
          newsLocale={newsPublisher.language.code}
          utmLink={utm_link}
          publisherName={newsPublisher.name}
          onelink={onelink}
        />
      </div>
      <div className="mt-8 flex items-center justify-end">
        <NewsActionButtons news={news} />
      </div>
      <SwitchToAppDialog />
      {sponsoredContent?.elementType ===
      SponsoredClientDataElementType.clientCarousel ? (
        <SponsoredCarousel
          title={sponsoredContent.data.title}
          brandIcon={sponsoredContent.data.brandIcon}
          items={sponsoredContent.data.items}
          variant={sponsoredContent.data.variant}
        />
      ) : sponsoredContent?.elementType ===
        SponsoredClientDataElementType.clientCard ? (
        <SponsoredCard
          title={sponsoredContent.data.title}
          imageUrl={sponsoredContent.data.imageUrl}
          link={sponsoredContent.data.link || ""}
          variant={
            sponsoredContent.data.variant || SponsoredCarouselVariant.square
          }
        />
      ) : null}
    </>
  );
};

const ReadMoreSection: React.FunctionComponent<{
  newsLocale: string;
  utmLink: string;
  publisherName: string;
  onelink: string;
}> = ({ newsLocale, utmLink, publisherName, onelink }) => {
  const switchToApp = onelink;

  const ctaData = {
    en: {
      ctaImage: ReadWallEn,
      ctaText:
        '"A great way to read the news. Love the UI, easy to use. The only news app for me!"',
      ctaButtonText: "Continue on the app",
    },
    ms: {
      ctaImage: ReadWallMs,
      ctaText:
        '"Cara terbaik untuk baca berita. UI menarik, mudah digunakan. Satu-satunya aplikasi berita untuk saya!"',
      ctaButtonText: "Teruskan di aplikasi",
    },
    zh: {
      ctaImage: ReadWallZh,
      ctaText:
        '"看新闻真的变享受了～ 界面清爽又好用，是目前用过体验最好的新闻 APP!"',
      ctaButtonText: "前往 APP 阅读完整内容",
    },
  };

  const selectedCta = ctaData[newsLocale] ?? ctaData.en;

  return (
    <>
      <div className="mb-4 md:flex md:flex-row md:items-center">
        <div>
          <Image src={selectedCta.ctaImage} alt="read-wall-cta-image" />
        </div>
        <div className="-mt-10 flex flex-col items-center gap-y-4 md:relative">
          <p className="text-center font-poppins text-text-darkest">
            {selectedCta.ctaText}
          </p>
          <LandingDownloadStarRatings showStarRating={false} />
          <Link href={switchToApp}>
            <a className="mt-4 inline-flex">
              <div className="flex items-center rounded-2xl bg-primary px-4 py-4">
                <p className="text-center font-sen text-white">
                  {selectedCta.ctaButtonText}
                </p>
              </div>
            </a>
          </Link>
        </div>
      </div>
      <div className="flex justify-center">
        <a
          href={utmLink}
          target="_blank"
          rel="noreferrer"
          className="cursor-pointer font-sen text-xs text-text-lightest hover:underline"
        >
          <Trans>Read the rest on</Trans> {publisherName}
        </a>
      </div>
    </>
  );
};

const NewsListSection = ({ news }) => {
  return (
    <>
      <div className="flex flex-col items-stretch divide-y divide-light-background">
        {news.map((news) => (
          <div className="py-4" key={news.id}>
            <NewNewsCard news={news} showPublisher />
          </div>
        ))}
      </div>
    </>
  );
};

export default NewsSummaryPage;
